'use client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFocusWithin, useSwipe } from '@THC/hooks';
import { filterDataProps } from '@THC/utils/helpers';
import { toastEmitter } from '../Toaster/ToastEmitter';
import { TOAST_PERSISTENCE_TIME } from './Toast.consts';
import styles from '../Toast.shared.module.scss';
export const useToast = (props, { toastRef }) => {
    const { content, heading, variant = 'default', ...toastProps } = props;
    const { __internal, duration = TOAST_PERSISTENCE_TIME, id, important = false, isStatic = false, onClose, persistent = isStatic, ...rest } = toastProps;
    const { toastId } = __internal || {};
    const dataProps = filterDataProps(rest);
    const toasterPosition = dataProps['data-toaster-position'];
    const [isToastDismissed, setIsToastDismissed] = useState(false);
    const [isToastMounted, setIsToastMounted] = useState(false);
    const isToastDismissedRef = useRef(false);
    const previousActiveElementRef = useRef(null);
    const primaryToastTimeoutIdRef = useRef(null);
    const backupDismissTimeoutIdRef = useRef(null);
    const timeoutActivationTimestampRef = useRef(null);
    const focusNextToast = () => {
        if (!toastId) {
            return;
        }
        const allToasts = Array.from(document.querySelectorAll('[data-thc-toast-id]'));
        const currentToast = allToasts.find((toast) => toast.getAttribute('data-thc-toast-id') === toastId);
        const nextToast = currentToast?.nextElementSibling;
        const prevToast = currentToast?.previousElementSibling;
        if (nextToast) {
            nextToast.focus();
        }
        else if (prevToast) {
            prevToast.focus();
        }
        else if (previousActiveElementRef.current) {
            previousActiveElementRef.current.focus();
        }
        else {
            document.body.focus();
        }
    };
    const removeToast = useCallback(() => {
        if (toastId) {
            toastEmitter.emit('remove', toastId);
        }
    }, [toastId]);
    const dismissToast = useCallback(() => {
        isToastDismissedRef.current = true;
        setIsToastDismissed(true);
        setIsToastMounted(false);
        backupDismissTimeoutIdRef.current = setTimeout(removeToast, Number(styles.transitionDurationRaw));
    }, [removeToast]);
    const clearToastAutoDismissTimeout = useCallback((params) => {
        if (params?.toastId && params.toastId !== toastId) {
            return;
        }
        if (primaryToastTimeoutIdRef.current) {
            clearTimeout(primaryToastTimeoutIdRef.current);
        }
    }, [toastId]);
    const setToastAutoDismissTimeout = useCallback((params) => {
        if (params?.toastId && params.toastId !== toastId) {
            return;
        }
        clearToastAutoDismissTimeout(null);
        const timeoutActivationTimestamp = timeoutActivationTimestampRef.current;
        const timeLeftUntilDismiss = timeoutActivationTimestamp
            ? Math.max(duration - (Date.now() - timeoutActivationTimestamp), 0)
            : 0;
        primaryToastTimeoutIdRef.current = setTimeout(dismissToast, duration + timeLeftUntilDismiss);
        timeoutActivationTimestampRef.current = +Date.now();
    }, [clearToastAutoDismissTimeout, dismissToast, duration, toastId]);
    const swipeProps = useSwipe({
        callbacks: {
            onTouchEnd: (e) => {
                const targetElement = e.target;
                if (targetElement?.closest?.('button, a')) {
                    return;
                }
                toastEmitter.emit('resume', null);
            },
            onTouchStart: (e) => {
                const targetElement = e.target;
                if (targetElement?.closest?.('button, a')) {
                    return;
                }
                toastEmitter.emit('pause', null);
            },
        },
        ...(toasterPosition === 'top-right'
            ? {
                onSwipeRight: dismissToast,
                springX: true,
            }
            : {
                onSwipeDown: dismissToast,
                springY: true,
            }),
    });
    const handleKeyDown = (event) => {
        if (event.key === 'Escape' && !isToastDismissed) {
            dismissToast();
            focusNextToast();
        }
    };
    useFocusWithin(toastRef, ({ hasFocus }) => {
        const eventParams = toastId ? { toastId } : null;
        if (hasFocus) {
            toastEmitter.emit('pause', eventParams);
        }
        else {
            toastEmitter.emit('resume', eventParams);
        }
    });
    useEffect(() => {
        const toastElement = toastRef.current;
        const transitionEndHandler = (e) => {
            if (!isToastDismissedRef.current) {
                return;
            }
            if (e.propertyName !== 'grid-template-rows') {
                return;
            }
            if (backupDismissTimeoutIdRef.current) {
                clearTimeout(backupDismissTimeoutIdRef.current);
            }
            removeToast();
        };
        if (toastElement) {
            toastElement.addEventListener('transitionend', transitionEndHandler);
        }
        return () => {
            if (toastElement) {
                toastElement.removeEventListener('transitionend', transitionEndHandler);
            }
        };
    }, [removeToast, toastRef]);
    useEffect(() => {
        const backupDismissTimeoutId = backupDismissTimeoutIdRef.current;
        return () => {
            if (backupDismissTimeoutId) {
                clearTimeout(backupDismissTimeoutId);
            }
        };
    }, []);
    useEffect(() => {
        if (persistent) {
            return undefined;
        }
        setToastAutoDismissTimeout(null);
        return () => {
            clearToastAutoDismissTimeout(null);
        };
    }, [clearToastAutoDismissTimeout, persistent, setToastAutoDismissTimeout]);
    useEffect(() => {
        if (persistent) {
            return undefined;
        }
        toastEmitter.on('pause', clearToastAutoDismissTimeout);
        toastEmitter.on('resume', setToastAutoDismissTimeout);
        return () => {
            toastEmitter.off('pause', clearToastAutoDismissTimeout);
            toastEmitter.off('resume', setToastAutoDismissTimeout);
        };
    }, [clearToastAutoDismissTimeout, persistent, setToastAutoDismissTimeout]);
    useEffect(() => {
        const handler = (params) => {
            if (params?.toastId && params.toastId !== toastId) {
                return;
            }
            dismissToast();
        };
        toastEmitter.on('dismiss', handler);
        return () => {
            toastEmitter.off('dismiss', handler);
        };
    }, [dismissToast, toastId]);
    useEffect(() => {
        previousActiveElementRef.current = document.activeElement;
        if (important && toastRef.current) {
            toastRef.current.focus();
        }
    }, [toastRef, important]);
    useEffect(() => {
        const animationFrameId = requestAnimationFrame(() => {
            setIsToastMounted(true);
        });
        return () => {
            cancelAnimationFrame(animationFrameId);
        };
    }, []);
    return {
        infoBoxProps: {
            __internal: {
                withAriaRole: false,
            },
            content,
            heading,
            onClose: () => {
                dismissToast();
                onClose?.();
            },
            variant,
        },
        properties: {
            isToastMounted,
            isToastStatic: isStatic,
        },
        toastProps: {
            'aria-atomic': true,
            'aria-live': important ? 'assertive' : 'polite',
            'data-thc-toast-id': toastId,
            id,
            onKeyDown: handleKeyDown,
            role: 'status',
            tabIndex: 0,
            ...swipeProps,
            ...filterDataProps(rest),
        },
    };
};
